import { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageContent from 'components/PageContent';
import Countdown from 'components/CountDown';
import Modal from 'components/Modal';
import Button from 'components/Button';
import { IPaymentMethod, usePaymentMethods } from 'entities/PaymentMethods/sdk';
import { IFlightTicketProposal } from 'entities/Flights/sdk';
import { LanguageEnum } from 'utils/constants';

import Title from '../Title';
import PaymentMethodCard from '../PaymentMethodCard';
import TotalSection from '../TotalSection';
import { FlightOfferViewEnum } from '../constants';
import { KeyedMutator } from 'swr';
import Skeleton from 'components/Skeleton';
import { initiateFlightTicketsPayment } from 'entities/Payments/sdk';

interface IPaymentViewProps {
  flightTicketProposal: IFlightTicketProposal;
  viewRef: React.RefObject<FlightOfferViewEnum>;
  setView: Dispatch<SetStateAction<FlightOfferViewEnum>>;
  currency: string | null;
  setCurrency: Dispatch<SetStateAction<string | null>>;
  flightTicketProposalMutate: KeyedMutator<IFlightTicketProposal>;
}

const PaymentView: React.FC<IPaymentViewProps> = ({
  flightTicketProposal,
  setView,
  currency,
  setCurrency,
  flightTicketProposalMutate,
  viewRef,
}) => {
  const { t, i18n } = useTranslation();
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const { data: paymentMethods, isLoading } = usePaymentMethods({
    flightTicketProposalUUID: flightTicketProposal.uuid,
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<IPaymentMethod | null>(null);

  useEffect(() => {
    setSelectedPaymentMethod(null);
  }, [currency]);

  const selectedMethodTotalProposalPrice: number | null = useMemo(() => {
    if (!selectedPaymentMethod) {
      return null;
    }

    return selectedPaymentMethod.total_proposal_prices.find((price) => price.currency === currency)?.value ?? null;
  }, [selectedPaymentMethod, currency]);

  const handleCancelClick = useCallback(() => {
    setView(FlightOfferViewEnum.DETAILS);
  }, [setView]);

  const handleCountdownExpire = useCallback(() => {
    setIsExpiredModalOpen(true);
  }, []);

  const handleRefreshClick = useCallback(() => {
    window.location.reload();
  }, []);

  const handleProceedToPaymentClick = useCallback(async () => {
    if (!selectedPaymentMethod || !currency) {
      return;
    }

    setIsPaymentLoading(true);

    try {
      const response = await initiateFlightTicketsPayment({
        flightTicketProposalUUID: flightTicketProposal.uuid,
        selectedPaymentMethod: selectedPaymentMethod.method,
        currency: currency,
      });

      // Redirect only if the view is still PAYMENT (client could have clicked 'Cancel')
      if (response.checkout_url && viewRef.current === FlightOfferViewEnum.PAYMENT) {
        window.location.href = response.checkout_url;
        return;
      }

      // TODO: Show an error notification

      setIsPaymentLoading(false);
    } catch (error) {
      console.error('Error initiating payment:', error);
      // TODO: Show an error notification

      setIsPaymentLoading(false);
    }
  }, [flightTicketProposal.uuid, selectedPaymentMethod, currency, viewRef]);

  return (
    <Fragment>
      <PageContent>
        <div className='flex flex-col gap-4'>
          <div className='flex justify-between md:items-center flex-col  md:flex-row'>
            <Title text={t('payment.paymentMethod')} />
            <span className='font-[MuseoSansCyrl700] flex md:text-lg md:flex-col max-[300px]:flex-col'>
              <span>{t('flightOffer.offerExpiresIn')}:&nbsp;</span>
              <Countdown
                targetTs={new Date(flightTicketProposal.expiration_datetime).getTime()}
                onExpire={handleCountdownExpire}
              />
            </span>
          </div>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className='h-[89px] w-lg' />)
          ) : paymentMethods ? (
            <div className='flex flex-col gap-4 md:max-w-lg'>
              {paymentMethods
                .filter((method) => method.supported_currencies.includes(currency as string))
                .map((method) => {
                  const methodFee = method.applied_fees.find((fee) => fee.currency === currency);

                  if (!methodFee) {
                    return null;
                  }

                  return (
                    <PaymentMethodCard
                      key={method.id}
                      method={method.method}
                      selected={selectedPaymentMethod?.id === method.id}
                      handleSelect={() => setSelectedPaymentMethod(method)}
                      appliedFee={methodFee}
                      label={method.label[i18n.language as LanguageEnum]}
                    />
                  );
                })}
            </div>
          ) : (
            <p>Something went wrong.</p>
          )}
        </div>
      </PageContent>
      <TotalSection
        view={FlightOfferViewEnum.PAYMENT}
        isConfirmDisabled={selectedPaymentMethod === null}
        flightTicketProposal={flightTicketProposal}
        handleConfirmClick={handleProceedToPaymentClick}
        handleCancelClick={handleCancelClick}
        currency={currency}
        setCurrency={setCurrency}
        totalPriceIncludingFees={selectedMethodTotalProposalPrice}
        flightTicketProposalMutate={flightTicketProposalMutate}
        isConfirmLoading={isPaymentLoading}
      />
      <Modal
        open={isExpiredModalOpen}
        title={t('flightOffer.offerHasExpired')}
        content={
          <div className='w-full'>
            <div className='flex justify-center'>
              <Button onClick={handleRefreshClick}>{t('common.refresh')}</Button>
            </div>
          </div>
        }
        center
      ></Modal>
    </Fragment>
  );
};

export default PaymentView;
